/**
 * @file slot-win-presenter.js
 * @desc This module contains SlotWinPresenter class definition.
 * @author Igor Kling <kling.igor.ksi@gmail.com>
 * @version 1.0.dev
 * @copyright 2016 (c) KSI Corporation
 * */

/**
 * @requires {SymbolAnimation}
 * @requires {SlotLine}
 * @requires {LineBillet}
 * */
var linesConfig = require('cfg/lines-config').lines;
var frameConfig = require('cfg/frames-config').frame;

(/** @lends <global> */function (global) {
    'use strict';

    var Animation = require('./animation').Animation;

    var SlotLine = require('./slotline').SlotLine;

    var LineBillet = require('./linebillet').LineBillet;

    var SlotFrame = require('./slotframe').SlotFrame;

    /**
     * Constructs SlotWinPresenter.
     * @class
     * @constructor
     * @param {SlotMath} slotMath Slot mathematics as data source to represent proper wins
     * @param {Object} config Configuration
     * @param {Object} delegate Delegate which responds on win show event
     * @name SlotWinPresenter
     * @classdesc This is a supplementary unit which holds {@link SlotLine|lines}, {@link SlotBillet|billets}, {@link SlotFrame|frames}, {@link SymbolAnimation symbol animations} and does win show
     * by its own
     * @extends {PIXI.Container}
     * */
    var SlotWinPresenter = function (slotMath, config, delegate) {

        PIXI.Container.call(this);

        this.delegate = delegate;

        this.showWinInterrupted = false;

        this.hideBarrelElement = null;

        this.showBarrelElement = null;

        this._slotMath = slotMath;

        this._config = config;

        this._symbolAnimation = [];

        if (config.animation_frames_count) {
            for (var j = 0; j < slotMath.symbolsNumber(); ++j) {

                var xOffset = 0;
                var yOffset = 0;

                if (config.animation_offsets) {
                    xOffset = config.animation_offsets[j].x;
                    yOffset = config.animation_offsets[j].y;
                }

                var animationSpeed = 0.2;

                if (config.animation_speed) {
                    animationSpeed = config.animation_speed[j];
                }

                var frameName = config.symbol_animation_filename_pattern.$(j) + "_%03d.png";

                var symbols_positions_count = config.barrel.rows * config.barrel.reels;

                for (var i = 0; i < symbols_positions_count; ++i) {
                    var animation = Animation.byFrameName(frameName, config.animation_frames_count[j]);
                    animation.animationSpeed = animationSpeed;
                    animation.anchor.x = 0;
                    animation.anchor.y = 0;

                    var col = i / config.barrel.rows | 0;
                    var row = i % config.barrel.rows | 0;

                    animation.x = this.elX( config, col ) + xOffset;
                    animation.y = this.elY( config, row ) + yOffset;
                    animation.visible = false;
                    this.addChild(animation);
                    this._symbolAnimation.push(animation);
                }
            }
        }


        this.barrelUpperContainer = new PIXI.Container();
        this.barrelUpperContainer.position.set(0, 0);
        this.addChild(this.barrelUpperContainer);

        this._lines = [];
        for (var i = 0; i < this._slotMath.linesNumber(); ++i) {
            var line = new SlotLine(config.lines_filename_pattern.$(i), linesConfig[i],
                                    config.frames_filename_pattern.$(i), frameConfig, config.barrel);

            this._lines.push(line);
            this.addChild(line);

            this.hideLine(i);
        }

        this._scatterFrames = [];
        if (config.scatter_frames_filename_pattern !== undefined) {
            for (var i = 0; i < config.barrel.reels; ++i) {
                if (frameConfig) {
                    var frame = new SlotFrame(config.scatter_frames_filename_pattern.$(i), frameConfig);

                    var col = i;

                    frame.x = this.elX( config, col ) + ( config.barrel.frameOffset ? config.barrel.frameOffset.x : 0 );
                    //frame.y = this.elY( barrelConfig, row ) + ( this.config.barrel.frameOffset ? this.config.barrel.frameOffset.y : 0 );
                    frame.visible = false; // изначально не видима


                    this.addChild(frame);
                    this._scatterFrames.push(frame);
                }
            }
        }

        if (config.left_billets_on_filename_pattern && config.right_billets_on_filename_pattern) {
		    this._leftBillets = [];
		    for (var i = 0; i < this._slotMath.linesNumber(); ++i) {
		        var billet = new LineBillet(config.left_billets_on_filename_pattern.$(i), config.left_billets_off_filename_pattern && config.left_billets_off_filename_pattern.$(i));
		        billet.position.x = config.left_billets_coords[i].x;
		        billet.position.y = config.left_billets_coords[i].y;
		        // billet.anchor.y = 1;
		        this.addChild(billet);
		        this._leftBillets.push(billet);
		    }

		    this._rightBillets = [];
		    for (var i = 0; i < this._slotMath.linesNumber(); ++i) {
		        var billet = new LineBillet(config.right_billets_on_filename_pattern.$(i), config.right_billets_off_filename_pattern && config.right_billets_off_filename_pattern.$(i));
		        billet.position.x = config.right_billets_coords[i].x;
		        billet.position.y = config.right_billets_coords[i].y;
		        this.addChild(billet);
		        this._rightBillets.push(billet);
		    }
        }
        else if (config.billets_on_filename_pattern) {
            this._billets = [];
            for (var i = 0; i < this._slotMath.linesNumber(); ++i) {
                var billet = new LineBillet(config.billets_on_filename_pattern.$(i), config.billets_off_filename_pattern && config.billets_off_filename_pattern.$(i));
                billet.position.x = config.billets_coords[i].x;
                billet.position.y = config.billets_coords[i].y;
                this.addChild(billet);
                this._billets.push(billet);
            }
        }

    };

    SlotWinPresenter.prototype = Object.create(PIXI.Container.prototype);

    SlotWinPresenter.prototype.constructor = SlotWinPresenter;

    SlotWinPresenter.prototype.terminate = function () {

        if (this.timeID) {
            clearTimeout(this.timeID);
        }

        for(var i = 0; i < this._symbolAnimation.length; ++i) {
            this._symbolAnimation[i].stop();
        }

        this.removeChildren();
        this.destroy();

        this.delegate = null;
        this.showWinInterrupted = null;
        this.hideBarrelElement = null;
        this.showBarrelElement = null;
        this._slotMath = null;
        this._config = null;
        this._symbolAnimation = null;
        this.barrelUpperContainer = null;
        this._lines = null;
        this._scatterFrames = null;

        if (this._leftBillets) {
            this._leftBillets = null;
        }
        if (this._rightBillets) {
            this._rightBillets = null;
        }
        if (this._billets) {
            this._billets = null;
        }


    };

    /** Left global ( where 0 is leftmost window pixel) coordinate of reel symbol at specified column.
     * @param {Object} Slot config with barrel field.*/
    SlotWinPresenter.prototype.elX = function( c, column )
    {
        return c.barrel.position.x + column * ( c.barrel.cellSize.width + ( c.barrel.reelGap || 0 ) );
    }
    /** Top global (where 0 is uppermost window pixel) coordinate of reel symbol at specified row.
     * @param {Object} Slot config with barrel field.*/
    SlotWinPresenter.prototype.elY = function( c, row )
    {
        return c.barrel.position.y + row * c.barrel.cellSize.height;
    };

    /**
     * Handles starting symbol animation.
     * @param {Number} idx reel position
     * */
    SlotWinPresenter.prototype.onSymbolAnimationStart = function(idx) {
        var els = this._slotMath.slotSymbols();
        var symbols_positions_count = this._config.barrel.rows * this._config.barrel.reels;
        return this._symbolAnimation[els[idx] * symbols_positions_count + idx];
    };

    /**
     * Handles stopping symbol animation.
     * @param {Number} idx reel position
     * */
    SlotWinPresenter.prototype.onSymbolAnimationStop = function(idx) {
        var els = this._slotMath.slotSymbols();
        var symbols_positions_count = this._config.barrel.rows * this._config.barrel.reels;
        return this._symbolAnimation[els[idx] * symbols_positions_count + idx];
    };


    /**
     * Shows line with frames on specified positions.
     * @param {Number} line Line index
     * @param {Number[]} positions Array of reel positions. Positions which are out of line trajectory will be omitted.
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showCroppedLine = function(line, positions) {
        this._lines[line].cutPositions(positions);
        this._lines[line].fadeIn();
    };

    /**
     * Hides line with frames on specified positions.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.hideCroppedLine = function(line) {
        this._lines[line].fadeOut();
    };

    /**
     * Shows entire line.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showLine = function(line) {
        this._lines[line].fadeIn();
    };

    /**
     * Checks if specified line is visible or not.
     * @param {Number} line Line index
     * @returns {Boolean} true - lines is visible, false - line is hidden
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.lineIsVisible = function(line) {
        return this._lines[line].visible;
    };

    /**
     * Checks if active billets are visible or not.
     * @param {Number} line Line index
     * @returns {Boolean} true - billets are visible, false - billets are hidden
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.activeBilletsAreVisible = function(line) {
        if (this._billets) {
            return this._billets[line].activeBilletIsVisible();
        }
        else if (this._leftBillets && this._rightBillets) {
            return this._leftBillets[line].activeBilletIsVisible();
        }

        return false;
    };

    SlotWinPresenter.prototype.showScatterFrames = function () {
        if (this._scatterFrames.length > 0) {
            var scatterPosition = this._slotMath.scatterPositions();
            for (var i = 0; i < scatterPosition.length; ++i) {
                var col = scatterPosition[i] / this._config.barrel.rows | 0;
                var row = scatterPosition[i] % this._config.barrel.rows | 0;


                this._scatterFrames[col].y = this.elY( this._config, row) + ( this._config.barrel.frameOffset ? this._config.barrel.frameOffset.y : 0 );
                this._scatterFrames[col].visible = true;
            }
        }
    };

    SlotWinPresenter.prototype.hideScatterFrames = function () {
        if (this._scatterFrames.length > 0) {
            for (var i = 0; i < this._scatterFrames.length; ++i) {
                this._scatterFrames[i].visible = false;
            }
        }
    };

    /**
     * Hides line.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.hideLine = function(line) {
        this._lines[line].hide();
    };

    /**
     * Restores line cropped positions.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.restoreLine = function(line) {
        this._lines[line].restoreLine();
    };

    /**
     * Shows inactive billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showInactiveBillets = function (line) {

        if (this._billets) {
            this._billets[line].showOffBillet();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].showOffBillet();
            this._rightBillets[line].showOffBillet();
        }
    };

    /**
     * Hides inactive billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.hideInactiveBillets = function (line) {

        if (this._billets) {
            this._billets[line].hideOffBillet();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].hideOffBillet();
            this._rightBillets[line].hideOffBillet();
        }
    };

    /**
     * Shows active billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showActiveBillets = function(line) {

        if (this._billets) {
            this._billets[line].showOnBillet();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].showOnBillet();
            this._rightBillets[line].showOnBillet();
        }
    };

    /**
     * Hides active billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.hideActiveBillets = function(line) {

        if (this._billets) {
            this._billets[line].hideOnBillet();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].hideOnBillet();
            this._rightBillets[line].hideOnBillet();
        }
    };

    /**
     * Shows active billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.fadeInActiveBillets = function(line) {

        if (this._billets) {
            this._billets[line].fadeIn();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].fadeIn();
            this._rightBillets[line].fadeIn();
        }
    };

    /**
     * Hides active billets.
     * @param {Number} line Line index
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.fadeOutActiveBillets = function(line) {

        if (this._billets) {
            this._billets[line].fadeOut();
        }
        else if (this._leftBillets && this._rightBillets) {
            this._leftBillets[line].fadeOut();
            this._rightBillets[line].fadeOut();
        }
    };


    /**
     * Starts symbols animation.
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.startSymbolsAnimation = function() {

        var winSymbolsPositions = this._slotMath.winSymbolsPositions();

        var self = this;

        winSymbolsPositions.forEach(function(idx) {

            if (self.hideBarrelElement) {
                self.hideBarrelElement(idx);
            }

            var animation = self.onSymbolAnimationStart(idx);
            if (animation) {
                animation.visible = true;
                animation.gotoAndPlay(0);
            }
        });
    };

    /**
     * Stops symbols animation.
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.stopSymbolsAnimation = function() {

        var winSymbolsPositions = this._slotMath.winSymbolsPositions();

        var self = this;

        winSymbolsPositions.forEach(function(idx) {

            if (self.showBarrelElement) {
                self.showBarrelElement(idx);
            }

            var animation = self.onSymbolAnimationStop(idx);

            if (animation) {
                animation.visible = false;
                animation.stop();
            }
        });
    };

    /**
     * Starts win show process.
     * @param {Object} slotPresenter this
     * @param {Object} math Mathematics as data source to show wins properly
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showWin = function(slotPresenter, math) {

        this.showWinInterrupted = false;

        var winLines = math.winLines();

        var currentLineId = function () {
            return winLines[lineIdx];
        };

        var lineIdx = 0;

        var firstTime = true;

        var nextLineId = function () {
            lineIdx++;
            if (lineIdx >= winLines.length) {
                if (firstTime) {
                    if (self.delegate && self.delegate.startSecondaryShowWin) self.delegate.startSecondaryShowWin();
                }
                firstTime = false;
                lineIdx = 0;
            }
            return winLines[lineIdx];
        };

        this.timeID = null;

        var self = this;

        var showLinesOneByOne;

        var showScattersWin = function () {

            if (self.showWinInterrupted) return;

            self.showScatterFrames();

            if (self.delegate) self.delegate.scattersDidShow(math.scatterWin(), firstTime);

            //if (winLines.length > 0) {
                self.timeID = setTimeout(function () {

                    if (winLines.length > 0) {

                        nextLineId();

                        if (self.delegate) self.delegate.scattersDidHide();

                        self.hideScatterFrames();

                        showLinesOneByOne();
                    }
                    else {
                        if (firstTime) {
                            if (self.delegate && self.delegate.startSecondaryShowWin) self.delegate.startSecondaryShowWin();
                        }
                        firstTime = false;
                        if (self.delegate) self.delegate.scattersDidShow(math.scatterWin(), false);
                    }
                }, 1000);
            //}
        };

        var showNextLine = function showNextLine() {

            if (self.showWinInterrupted) return;

            var currentLine = currentLineId();

            // если показали последнюю линию и есть скаттеры
            if (lineIdx == winLines.length - 1 && math.scatterWin() > 0) {
                slotPresenter.hideActiveBillets(currentLine);
                slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                if (self.delegate) self.delegate.lineDidHide(currentLine, firstTime);

                self.timeID = setTimeout(function() {
                    showScattersWin();
                    // nextLineId();
                }, 1000);
            }
            else {
                var nextLine = nextLineId();

                if (currentLine != nextLine) {
                    slotPresenter.hideActiveBillets(currentLine);
                    slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                    if (self.delegate) self.delegate.lineDidHide(currentLine);

                    slotPresenter.showActiveBillets(nextLine);
                    slotPresenter.showCroppedLine(nextLine, math.lineWinPositions(nextLine));

                    if (self.delegate) self.delegate.lineDidShow(nextLine, math.lineWin(nextLine), firstTime);

                    self.timeID = setTimeout(showNextLine, 1000);
                }
            }
        };

        var showSingleLine = function showSingleLine() {
            if (self.showWinInterrupted) return;
            self.timeID = setTimeout(function() {
                if (firstTime) {
                    if (self.delegate && self.delegate.startSecondaryShowWin) self.delegate.startSecondaryShowWin();
                }
                firstTime = false;
                if (self.delegate) self.delegate.lineDidShow(currentLineId(), math.lineWin(currentLineId()), false);
                showSingleLine()
            }, 1000);
        };

        var showLinesOneByOne = function showLinesOneByOne() {

            if (self.showWinInterrupted) return;

            slotPresenter.showActiveBillets(currentLineId());
            slotPresenter.showCroppedLine(currentLineId(), math.lineWinPositions(currentLineId()));

            if (self.delegate) self.delegate.lineDidShow(currentLineId(), math.lineWin(currentLineId()), firstTime);

            if (winLines.length === 1 && math.scatterWin() === 0) {
                self.timeID = setTimeout(showSingleLine, 1000);
            }
            else {
                self.timeID = setTimeout(showNextLine, 1000);
            }
        };

        var hideAllWinLines = function hideAllWinLines () {

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.hideActiveBillets(winLines[i]);
                slotPresenter.hideLine(winLines[i]);
            }

            if (self.showWinInterrupted) return;

            self.timeID = setTimeout(showLinesOneByOne, 1000);
        };

        var showAllWinLines = function showAllWinLines() {

            if (self.showWinInterrupted) return;

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.showActiveBillets(winLines[i]);
                slotPresenter.showLine(winLines[i]);
            }

            self.timeID = setTimeout(hideAllWinLines, 1000);
        };

        if (this.delegate) this.delegate.winShowDidStart();

        slotPresenter.startSymbolsAnimation();

        // прячем плашки
        for (var i = 0; i < this._slotMath.actualLinesNumber(); ++i) {
            slotPresenter.showInactiveBillets(i);
            slotPresenter.hideActiveBillets(i);
        }

        //если есть выигрышные линии и возможно скаттеры
        if (winLines.length > 0) {
            this.timeID = setTimeout(showAllWinLines, 300); // дадим время погаснуть плашкам
        }
        // если только скаттеры выиграли
        else if(math.scatterWin() > 0) {
            this.timeID = setTimeout (showScattersWin, 300);
        }
    };

    /**
     * Interrupts show win process.
     * @param {Object} slotPresenter
     * @param {Object} math
     * @param {Bool} freegames is freegames already running
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.interruptShowWin = function(slotPresenter, math, freegames) {

        this.showWinInterrupted = true;
        clearTimeout(this.timeID);

        this.hideScatterFrames();

        for (var i = 0; i < this._slotMath.actualLinesNumber(); ++i) {
            this.hideLine(i);
            this.restoreLine(i);
        }

        for (var i = 0; i < this._slotMath.actualLinesNumber(); ++i) {
            slotPresenter.showActiveBillets(i);
        }

        slotPresenter.stopSymbolsAnimation(freegames);

        if (this.delegate) this.delegate.winShowDidFinish(freegames);
    };

    /**
     * Run task via specified delay
     * @param  {Function} task  function to run
     * @param  {Number} delay ms to wait before task will run
     */
    SlotWinPresenter.prototype.runDelayedTask = function(task, delay) {
        this.timeID = setTimeout(task, delay);
    };

    /**
     * Starts win show process before extra games.
     * @param {Object} slotPresenter this
     * @param {Object} math Mathematics as data source to show wins properly
     * @param {Function} done Callback to be called on show ending
     * @param {Bool} freegames is freegames already running
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showWinBeforeExtraGames = function(slotPresenter, math, done, freegames) {

        freegames = freegames || false;

        // ЭТУ ФУНКЦИЮ НУЖНО ЧИТАТЬ С КОНЦА ДО НАЧАЛА!!!

        this.showWinInterrupted = false;

        var winLines = math.winLines();

        var currentLineId = function () {
            return winLines[lineIdx];
        };

        var lineIdx = 0;

        var nextLineId = function () {
            lineIdx++;
            if (lineIdx >= winLines.length) {
                lineIdx = 0;
            }
            return winLines[lineIdx];
        };

        this.timeID = null;

        var self = this;

        var showLinesOneByOne;


        var finishShowWin = function () {
            if (self.delegate) {
                self.delegate.scattersDidHide();
            }
            self.interruptShowWin(slotPresenter, math, freegames);
            done();
        };

        var showScattersWin = function () {

            if (self.showWinInterrupted) {
                return;
            }

            if (self.delegate) {
                self.delegate.scattersDidShow(math.scatterWin(), true);
            }

            //if (winLines.length > 0) {
            //    self.timeID = setTimeout(function () {

            //        if (self.delegate) {
            //            self.delegate.scattersDidHide();
            //        }

            //        showLinesOneByOne();
            //    }, 1000);
            //}
            self.timeID = setTimeout(function () {

                if (self.delegate) {
                    self.delegate.scattersDidHide(true);
                }

                finishShowWin();
            }, 1500);
        };


        var showNextLine = function showNextLine() {

            if (self.showWinInterrupted) {
                return;
            }

            var currentLine = currentLineId();

            // если показали последнюю линию и есть скаттеры
            if (lineIdx == winLines.length - 1 ) {
                slotPresenter.hideActiveBillets(currentLine);
                slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                if (self.delegate) {
                    self.delegate.lineDidHide(currentLine, true);
                }

                // nextLineId();

                if (math.scatterWin() > 0) {
                    showScattersWin();
                }
                else {
                    self.timeID = setTimeout(finishShowWin, 1000);
                }
            }
            else {
                var nextLine = nextLineId();

                if (currentLine != nextLine) {
                    slotPresenter.hideActiveBillets(currentLine);
                    slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                    if (self.delegate) {
                        self.delegate.lineDidHide(currentLine, true);
                    }

                    slotPresenter.showActiveBillets(nextLine);
                    slotPresenter.showCroppedLine(nextLine, math.lineWinPositions(nextLine));

                    if (self.delegate) {
                        self.delegate.lineDidShow(nextLine, math.lineWin(nextLine), true);
                    }

                    self.timeID = setTimeout(showNextLine, 1000);
                }
            }
        };

        var showSingleLine = function showSingleLine() {
            if (self.showWinInterrupted) {
                return;
            }

            self.timeID = setTimeout(finishShowWin, 1000);
        };

        var showLinesOneByOne = function showLinesOneByOne() {

            if (self.showWinInterrupted) {
                return;
            }

            slotPresenter.showActiveBillets(currentLineId());
            slotPresenter.showCroppedLine(currentLineId(), math.lineWinPositions(currentLineId()));

            if (self.delegate) {
                self.delegate.lineDidShow(currentLineId(), math.lineWin(currentLineId()), true);
            }

            if (winLines.length === 1 && math.scatterWin() === 0) {
                self.timeID = setTimeout(finishShowWin, 1000);
            }
            else {
                self.timeID = setTimeout(showNextLine, 1000);
            }
        };

        var hideAllWinLines = function hideAllWinLines () {

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.hideActiveBillets(winLines[i]);
                slotPresenter.hideLine(winLines[i]);
            }

            if (self.showWinInterrupted) {
                return;
            }

            self.timeID = setTimeout(showLinesOneByOne, 1000);
        };

        // первая функция в цепочке показа выигрыша
        var showAllWinLines = function showAllWinLines() {

            if (self.showWinInterrupted) {
                return;
            }

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.showActiveBillets(winLines[i]);
                slotPresenter.showLine(winLines[i]);
            }

            self.timeID = setTimeout(hideAllWinLines, 1000);
        };



        // начало показа выигрыша (все что было до этого это приватные функции частей показа выигрыша)
        if (this.delegate) this.delegate.winShowDidStart(freegames);


        slotPresenter.startSymbolsAnimation();

        // прячем плашки
        for (var i = 0; i < this._slotMath.actualLinesNumber(); ++i) {
            slotPresenter.showInactiveBillets(i);
            slotPresenter.hideActiveBillets(i);
        }

        //если есть выигрышные линии и возможно скаттеры
        if (winLines.length > 0) {
            this.timeID = setTimeout(showAllWinLines, 300); // дадим время погаснуть плашкам перед показом всех выигрышных линий
        }
        // если только скаттеры выиграли
        else if(math.scatterWin() > 0) {
            /*if (this.delegate) {
                this.delegate.scattersDidShow(math.scatterWin());
            }
            // они и так должны анимироваться, анимируем и завершаем показ
            this.timeID = setTimeout(finishShowWin, 2000)*/
            this.timeID = setTimeout(showScattersWin, 500);
        }
        else if (math.hasBonus()) {
            // если выпал бонус при осуствии выигрыша за спин, тогда просто ждем конца анимации
            this.timeID = setTimeout(finishShowWin, 2000)
        }
     };

    /**
     * Starts win show process before on freespin win.
     * @param {Object} slotPresenter this
     * @param {Object} math Mathematics as data source to show wins properly
     * @param {Function} done Callback to be called on show ending
     * @memberOf SlotWinPresenter
     * */
    SlotWinPresenter.prototype.showFreeGamesSpinWin = function(slotPresenter, math, done) {

        // ЭТУ ФУНКЦИЮ НУЖНО ЧИТАТЬ С КОНЦА ДО НАЧАЛА!!!

        this.showWinInterrupted = false;

        var winLines = math.winLines();

        var currentLineId = function () {
            return winLines[lineIdx];
        };

        var lineIdx = 0;

        var nextLineId = function () {
            lineIdx++;
            if (lineIdx >= winLines.length) {
                lineIdx = 0;
            }
            return winLines[lineIdx];
        };

        this.timeID = null;

        var self = this;

        var showLinesOneByOne;


        var finishShowWin = function () {
            if (self.delegate) {
                self.delegate.scattersDidHide();
            }
            self.interruptShowWin(slotPresenter, math, true/*freegames*/);
            done();
        };

        var showScattersWin = function () {

            if (self.showWinInterrupted) {
                return;
            }

            if (self.delegate) {
                self.delegate.scattersDidShow(math.scatterWin(), true);
            }

            // if (winLines.length > 0) {
            //     self.timeID = setTimeout(function () {
            //
            //         if (self.delegate) {
            //             self.delegate.scattersDidHide();
            //         }
            //
            //         showLinesOneByOne();
            //     }, 1000);
            // }

            self.timeID = setTimeout(function () {

                if (self.delegate) {
                    self.delegate.scattersDidHide();
                }

                finishShowWin();
            }, 1500);
        };


        var showNextLine = function showNextLine() {

            if (self.showWinInterrupted) {
                return;
            }

            var currentLine = currentLineId();

            // если показали последнюю линию
            if (lineIdx == winLines.length - 1) {
                slotPresenter.hideActiveBillets(currentLine);
                slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                if (self.delegate) {
                    self.delegate.lineDidHide(currentLine, true);
                }

                if (math.scatterWin() > 0) {
                    showScattersWin();
                }
                else {
                    self.timeID = setTimeout(finishShowWin, 1000);
                }
            }
            else {
                var nextLine = nextLineId();

                if (currentLine != nextLine) {
                    slotPresenter.hideActiveBillets(currentLine);
                    slotPresenter.hideCroppedLine(currentLine, math.lineWinPositions(currentLine));

                    if (self.delegate) {
                        self.delegate.lineDidHide(currentLine, true);
                    }

                    slotPresenter.showActiveBillets(nextLine);
                    slotPresenter.showCroppedLine(nextLine, math.lineWinPositions(nextLine));

                    if (self.delegate) {
                        self.delegate.lineDidShow(nextLine, math.lineWin(nextLine), true);
                    }

                    self.timeID = setTimeout(showNextLine, 1000);
                }
            }
        };

        var showSingleLine = function showSingleLine() {
            if (self.showWinInterrupted) {
                return;
            }

            self.timeID = setTimeout(finishShowWin, 1000);
        };

        var showLinesOneByOne = function showLinesOneByOne() {

            if (self.showWinInterrupted) {
                return;
            }

            slotPresenter.showActiveBillets(currentLineId());
            slotPresenter.showCroppedLine(currentLineId(), math.lineWinPositions(currentLineId()));

            if (self.delegate) {
                self.delegate.lineDidShow(currentLineId(), math.lineWin(currentLineId()), true);
            }

            if (winLines.length === 1 && math.scatterWin() === 0) {
                self.timeID = setTimeout(finishShowWin, 1000);
            }
            else {
                self.timeID = setTimeout(showNextLine, 1000);
            }
        };

        var hideAllWinLines = function hideAllWinLines () {

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.hideActiveBillets(winLines[i]);
                slotPresenter.hideLine(winLines[i]);
            }

            if (self.showWinInterrupted) {
                return;
            }

            self.timeID = setTimeout(showLinesOneByOne, 1000);
        };

        // первая функция в цепочке показа выигрыша
        var showAllWinLines = function showAllWinLines() {

            if (self.showWinInterrupted) {
                return;
            }

            for (var i = 0; i < winLines.length; ++i) {
                slotPresenter.showActiveBillets(winLines[i]);
                slotPresenter.showLine(winLines[i]);
            }

            self.timeID = setTimeout(hideAllWinLines, 1000);
        };



        // начало показа выигрыша (все что было до этого это приватные функции частей показа выигрыша)
        if (this.delegate) {
            this.delegate.winShowDidStart(true/*freegames*/);
        }

        slotPresenter.startSymbolsAnimation();

        // прячем плашки
        for (var i = 0; i < this._slotMath.actualLinesNumber(); ++i) {
            slotPresenter.showInactiveBillets(i);
            slotPresenter.hideActiveBillets(i);
        }

        //если есть выигрышные линии и возможно скаттеры
        if (winLines.length > 0) {
            this.timeID = setTimeout(showAllWinLines, 300); // дадим время погаснуть плашкам перед показом всех выигрышных линий
        }
        // если только скаттеры выиграли
        else if(math.scatterWin() > 0) {
            /*if (this.delegate) {
                this.delegate.scattersDidShow(math.scatterWin());
            }
            // они и так должны анимироваться, анимируем и завершаем показ
            this.timeID = setTimeout(finishShowWin, 2000)*/
            this.timeID = setTimeout(showScattersWin, 500);
        }
    };



    global.SlotWinPresenter = SlotWinPresenter;
}(typeof module === 'object' && module.exports || this));



//////////////////
// WEBPACK FOOTER
// ./src/library/slot-win-presenter.js
// module id = 129
// module chunks = 0